/**
 * StoragePlus - 分离式存储工具类————增加命名空间，处理序列化反序列化
 * 【已序列化不在序列化，未序列化执行序列化】
 * 提供与原生API风格一致的 localStorage、sessionStorage 和 Cookie 操作接口
 * Cookie本身的作用域是整个域名下，可设置路径，但没用端口隔离的概念
 */
(function (global, factory) {
  // 用于模块化环境中的导出
  const exportObject = factory();

  // 在模块化环境中导出
  typeof exports === 'object' && typeof module !== 'undefined' ?
    module.exports = exportObject :
    typeof define === 'function' && define.amd ?
      define(factory) :
      // 直接挂载到全局对象
      Object.assign(global, exportObject);
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // 集成项目之间，项目各个端之间隔离（规避部署到一个域名，登录互相覆盖问题），建议 项目_终端_key
  // 挂载的从vue注册的ops里取（存到localStorage里中转）, 找不到就是空
  // 因为静态资源和vue install加载时机问题 避免在模块加载时静态初始化 namespace，而是通过一个函数动态获取当前的命名空间， 仅读取性能影响可以忽略不计
  const getNamespace = () => localStorage.getItem('namespace') || '';

  /**
   * localStorage 增强工具
   */
  const localStoragePlus = {
    /**
     * 设置本地存储项
     * @param {string} key - 键名
     * @param {*} value - 键值
     * @returns {boolean} 操作是否成功
     */
    setItem(key, value) {
      try {
        const serializedValue = serializeValue(value);
        localStorage.setItem(getNamespace() + key, serializedValue);
        return true;
      } catch (e) {
        console.error('localStorage setItem error:', e);
        return false;
      }
    },

    /**
     * 获取本地存储项
     * @param {string} key - 键名
     * @returns {*} 存储的值
     */
    getItem(key) {
      try {
        const item = localStorage.getItem(getNamespace() + key);
        if (item === null) return null;
        return deserializeValue(item);
      } catch (e) {
        console.error('localStorage getItem error:', e);
        return null;
      }
    },

    /**
     * 移除本地存储项
     * @param {string} key - 键名
     * @returns {boolean} 操作是否成功
     */
    removeItem(key) {
      try {
        localStorage.removeItem(getNamespace() + key);
        return true;
      } catch (e) {
        console.error('localStorage removeItem error:', e);
        return false;
      }
    },

    /**
     * 清空命名空间下的所有本地存储项
     * @returns {boolean} 操作是否成功
     */
    clear() {
      try {
        // 只删除命名空间下的项，而不是清除所有
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith(getNamespace())) {
            localStorage.removeItem(key);
          }
        }
        return true;
      } catch (e) {
        console.error('localStorage clear error:', e);
        return false;
      }
    },

    /**
     * 获取命名空间下本地存储项数量
     * get是ES5引入的getter访问器属性（用于获取对象属性的值，但使用时像访问普通属性一样，就是使用时没有{}） 同理obj.name就是触发了set 方法（我们可以加trim等处理逻辑）
     * @returns {number} 存储项数量
     */
    get length() {
      try {
        let count = 0;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(getNamespace())) {
            count++;
          }
        }
        return count;
      } catch (e) {
        console.error('localStorage length error:', e);
        return 0;
      }
    },
  };

  /**
   * sessionStorage 增强工具
   */
  const sessionStoragePlus = {
    /**
     * 设置会话存储项
     * @param {string} key - 键名
     * @param {*} value - 键值
     * @returns {boolean} 操作是否成功
     */
    setItem(key, value) {
      try {
        const serializedValue = serializeValue(value);
        sessionStorage.setItem(getNamespace() + key, serializedValue);
        return true;
      } catch (e) {
        console.error('sessionStorage setItem error:', e);
        return false;
      }
    },

    /**
     * 获取会话存储项
     * @param {string} key - 键名
     * @returns {*} 存储的值
     */
    getItem(key) {
      try {
        const item = sessionStorage.getItem(getNamespace() + key);
        if (item === null) return null;
        return deserializeValue(item);
      } catch (e) {
        console.error('sessionStorage getItem error:', e);
        return null;
      }
    },

    /**
     * 移除会话存储项
     * @param {string} key - 键名
     * @returns {boolean} 操作是否成功
     */
    removeItem(key) {
      try {
        sessionStorage.removeItem(getNamespace() + key);
        return true;
      } catch (e) {
        console.error('sessionStorage removeItem error:', e);
        return false;
      }
    },

    /**
     * 清空命名空间下的所有会话存储项
     * @returns {boolean} 操作是否成功
     */
    clear() {
      try {
        // 只删除命名空间下的项，而不是清除所有
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(getNamespace())) {
            sessionStorage.removeItem(key);
          }
        }
        return true;
      } catch (e) {
        console.error('sessionStorage clear error:', e);
        return false;
      }
    },

    /**
     * 获取命名空间下会话存储项数量
     * @returns {number} 存储项数量
     */
    get length() {
      try {
        let count = 0;
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(getNamespace())) {
            count++;
          }
        }
        return count;
      } catch (e) {
        console.error('sessionStorage length error:', e);
        return 0;
      }
    },
  };

  /**
   * Cookie 工具
   */
  const cookiePlus = {
    /**
     * 设置 Cookie
     * @param {string} key - 键名
     * @param {*} value - 键值
     * @param {object} options - 配置选项
     * @param {number|Date} options.expires - 过期时间（天数或日期对象）
     * @param {string} options.path - 路径（默认 /）
     * @param {string} options.domain - 域名（域名或子域名）
     * @param {boolean} options.secure - 是否仅 HTTPS（默认false）
     * @param {string} options.sameSite - SameSite 策略（默认Lax宽松模式）
     * @returns {boolean} 操作是否成功
     */
    setItem(key, value, options = {}) {
      try {
        const serializedValue = serializeValue(value);
        const encodedValue = encodeURIComponent(serializedValue);
        let cookieStr = `${getNamespace() + key}=${encodedValue}`;

        if (options.expires) {
          // 兼容天数和过期时间（日期对象）两种格式
          if (typeof options.expires === 'number') {
            const days = options.expires;
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            cookieStr += `;expires=${expires.toUTCString()}`;
          } else if (options.expires.toUTCString) {
            cookieStr += `;expires=${options.expires.toUTCString()}`;
          }
        }

        if (options.path) {
          cookieStr += `;path=${options.path}`;
        } else {
          cookieStr += ';path=/';
        }

        if (options.domain) {
          cookieStr += `;domain=${options.domain}`;
        }

        if (options.secure) {
          cookieStr += ';secure';
        }

        if (options.sameSite) {
          cookieStr += `;samesite=${options.sameSite}`;
        }

        document.cookie = cookieStr;
        return true;
      } catch (e) {
        console.error('cookie setItem error:', e);
        return false;
      }
    },

    /**
   * 获取 Cookie 值（浏览器已自动过滤过期项）
   * @param {string} key - 键名
   * @returns {*} 存储的值
   */
    getItem(key) {
      try {
        const name = `${getNamespace() + key}=`;
        const cookiePairs = document.cookie.split(';');

        for (let i = 0; i < cookiePairs.length; i++) {
          let cookie = cookiePairs[i].trim();

          // 浏览器已经过滤了过期的 Cookie，所以这里获取到的都是有效的
          if (cookie.startsWith(name)) {
            const value = cookie.substring(name.length);
            const decodedValue = decodeURIComponent(value);
            return deserializeValue(decodedValue);
          }
        }
        return null;
      } catch (e) {
        console.error('cookie getItem error:', e);
        return null;
      }
    },

    /**
     * 移除 Cookie
     * @param {string} key - 键名
     * @param {object} options - 配置选项
     * @param {string} options.path - 路径
     * @param {string} options.domain - 域名
     * @returns {boolean} 操作是否成功
     */
    removeItem(key, options = {}) {
      try {
        const opts = { ...options, expires: -1 };
        return this.setItem(key, '', opts);
      } catch (e) {
        console.error('cookie removeItem error:', e);
        return false;
      }
    },

    /**
     * 清空命名空间下的所有 Cookie
     * @returns {boolean} 操作是否成功
     */
    clear() {
      try {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const fullName = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          // 只删除命名空间下的 Cookie
          if (fullName.startsWith(getNamespace())) {
            // 提取不带命名空间的 key
            const key = fullName.substring(getNamespace().length);
            this.removeItem(key);
          }
        }
        return true;
      } catch (e) {
        console.error('cookie clear error:', e);
        return false;
      }
    }
  };

  /**
   * 检查值是否已经序列化
   * @param {*} value - 要检查的值
   * @returns {boolean} 是否已经序列化
   */
  function isSerialized(value) {
    if (typeof value !== 'string') return false;
    try {
      JSON.parse(value);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 序列化值
   * @param {*} value - 要序列化的值
   * @returns {string} 序列化后的字符串
   */
  function serializeValue(value) {
    if (isSerialized(value)) {
      return value;
    }
    return JSON.stringify(value);
  }

  /**
   * 反序列化值
   * @param {string} value - 要反序列化的值
   * @returns {*} 反序列化后的值
   */
  function deserializeValue(value) {
    try {
      // 尝试解析为JSON
      return JSON.parse(value);
    } catch (parseError) {
      // 如果解析失败，返回原始字符串值
      return value;
    }
  }

  // 暴露公共方法--直接暴露到window上，没用命名空间隔离
  return {
    localStoragePlus,
    sessionStoragePlus,
    cookiePlus
  };
}));